import React, { useCallback, useEffect, useState } from 'react'
import { addWeeks, format, formatISO, isSameWeek } from 'date-fns'
import { Spinner, Select, Button, Typography, toast } from '@telavita-core/react-design-kit'

import { NonRecurrentReservationBox } from 'components'
import { Steps, Reservation } from '../../'
import { Patient } from '../../types'

import reservationService, {
  Availabilities,
  WeekDays,
  Availability,
  ProfessionalRestrictions,
} from 'services/reservation/'

import * as S from './styles'

const weekDays: WeekDays = {
  '0': 'Segunda-feira',
  '1': 'Terça-feira',
  '2': 'Quarta-feira',
  '3': 'Quinta-feira',
  '4': 'Sexta-feira',
  '5': 'Sábado',
  '6': 'Domingo',
}

interface Option {
  id: number
  label: string
}

interface ScheduleOption extends Option {
  handler?: (option?: Option) => Availability
}

interface CreateReservationProps {
  patient: Patient
  professionalUsername: string
  onChangeStep: (step: Steps) => void
  onCreateReservation: (reservation: Reservation) => void
}

export function CreateReservation({
  patient,
  professionalUsername,
  onChangeStep,
  onCreateReservation,
}: CreateReservationProps): JSX.Element {
  const [loading, setLoading] = useState(false)
  const [loadingProfessionalRestrictions, setLoadingProfessionalRestrictions] = useState(false)
  const [availabilities, setAvailabilities] = useState<Availabilities>({} as Availabilities)
  const [days, setDays] = useState<Option[]>([])
  const [selectedDay, setSelectedDay] = useState<Option>(null)
  const [schedules, setSchedules] = useState<ScheduleOption[]>([])
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleOption>(null)
  const [restrictions, setRestrictions] = useState<Date[]>([])
  const [professionalRestrictions, setProfessionalRestrictions] = useState<ProfessionalRestrictions>({} as ProfessionalRestrictions)
  const [nonRecurrentReservationDates, setNonRecurrentReservationDates] = useState<string[]>([])
  const [disableCreateReservationButton, setDisableCreateReservationButton] = useState(false)

  const showScheduleSelect = selectedDay !== null
  const showSubmitButton = selectedDay !== null && selectedSchedule !== null && !loadingProfessionalRestrictions
  const hasProfessionalRestrictions = restrictions.length > 0
  const hasNonRecurrentReservationDates = nonRecurrentReservationDates
    .every(date => Boolean(date)) && nonRecurrentReservationDates.length > 0

  function handleNonRecurrentReservationDate(date: string, slotIndex: number) {
    const newNonRecurrentReservationDates = [...nonRecurrentReservationDates]
    newNonRecurrentReservationDates[slotIndex] = date
    setNonRecurrentReservationDates(newNonRecurrentReservationDates)
  }

  useEffect(() => {
    if (hasProfessionalRestrictions || !hasNonRecurrentReservationDates) {
      setDisableCreateReservationButton(true)
    } 

    if ((hasProfessionalRestrictions && hasNonRecurrentReservationDates) || !hasProfessionalRestrictions) {
      setDisableCreateReservationButton(false)
    }

  }, [
    nonRecurrentReservationDates, 
    restrictions, 
    hasNonRecurrentReservationDates, 
    hasProfessionalRestrictions
  ])

  function handleSelectDay(day: Option) {
    setSelectedSchedule(null)
    setSelectedDay(day)
  }

  function handleSelectSchedule(schedule: Option) {
    setSelectedSchedule(schedule)
  }

  const nonRecurrentReservationsDates = nonRecurrentReservationDates?.map(dateString => {
    if (!dateString) return null

    const startDate = new Date(dateString)
    const endDate = new Date(startDate)
    endDate.setMinutes(startDate.getMinutes() + 59)
    endDate.setSeconds(startDate.getSeconds() + 59)

    return {
      start_date: formatISO(startDate),
      end_date: formatISO(endDate),
    }
  })

  async function handleCreateReservation() {
    setLoading(true)

    try {
      const response = await reservationService.createReservation({
        professionalUsername,
        patientUsername: patient.username,
        reservationStartDate: professionalRestrictions.reservationStartDate,
        reservationEndDate: professionalRestrictions.reservationEndDate,
        recurrent: true,
        nonRecurrentReservations: hasNonRecurrentReservationDates ? nonRecurrentReservationsDates : null
      })
  
      onChangeStep(Steps.SUCCESS)
      onCreateReservation({
        ...response,
        hasRestrictions: restrictions.length > 0,
      })
      setLoading(false)
    } catch (err) {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!selectedDay || !selectedSchedule) return

    async function verifyProfessionalRestrictions() {
      setLoadingProfessionalRestrictions(true)
      const selectedDates = selectedSchedule.handler()
      const isSelectedDateInSameWeekAsNow = isSameWeek(new Date(), new Date(selectedDates.startDate))
      const startDate = isSelectedDateInSameWeekAsNow ? addWeeks(selectedDates.startDate, 1) : selectedDates.startDate
      const endDate = isSelectedDateInSameWeekAsNow ? addWeeks(selectedDates.endDate, 1) : selectedDates.endDate

      const response = await reservationService.professionalRestrictions({
        professionalUsername,
        patientUsername: patient.username,
        startDate,
        endDate,
        planCode: patient.lastSchedulePlanCode
      })

      setRestrictions(response.restrictions)
      setProfessionalRestrictions(response)
      setLoadingProfessionalRestrictions(false)
    }

    void verifyProfessionalRestrictions()
  }, [selectedDay, selectedSchedule, patient, professionalUsername])

  useEffect(() => {
    if (!selectedDay) return

    function getSchedulesFromSelectedDay() {
      const { id } = selectedDay
      const schedules = availabilities[id] as Availability[]
  
      return schedules.map((schedule, index) => {
        return {
          id: index,
          label: format(schedule.startDate, 'HH:mm'),
          handler: () => schedule
        } as ScheduleOption
      })
    }

    const schedules = getSchedulesFromSelectedDay() 
    setSchedules(schedules)
  }, [selectedDay, availabilities])

  const handleProfessionalWithNoAvailabilities = useCallback(() => {
    toast({
      text: 'Não há horários disponíveis para reserva.',
      type: 'error',
      position: 'top-right',
      autoClose: 5000,
    })
    setLoading(false)
    return onChangeStep(Steps.FIND_PATIENT)
  }, [onChangeStep])

  useEffect(() => {
    async function getProfessionalAvailabilities() {
      try {
        const response = await reservationService.availabilities({
          professionalUsername,
          patientUsername: patient.username,
        })
    
        setAvailabilities(response.availabilities)
  
        const dayOptions: Option[] = []
        
        Object.entries(response.availabilities).forEach(([wd, av]) => {
          if (av.length > 0) {
            const day: Option = {
              id: Number(wd),
              label: weekDays[wd] as string,
            }
            
            dayOptions.push(day)
          }
        })
        
        if (!dayOptions.length) {
          return handleProfessionalWithNoAvailabilities()
        }

        setDays(dayOptions)

      } catch (err) {
        return handleProfessionalWithNoAvailabilities()
      } finally {
        setLoading(false)
      }
    }

    void getProfessionalAvailabilities()
  }, [patient, professionalUsername, handleProfessionalWithNoAvailabilities])

  if (loading || !days.length) {
    return (
      <S.Loading>
        <Spinner />
      </S.Loading>
    )
  }

  return (
    <S.CreateReservationContainer>
      
      <Select
        options={days}
        selected={selectedDay}
        onSelect={handleSelectDay}
        position='fixed'
        label='Escolha um dia da semana'
      />

      {showScheduleSelect && (
        <Select
          options={schedules}
          selected={selectedSchedule}
          onSelect={handleSelectSchedule}
          position='fixed'
          label='Escolha um de seus horários recorrentes'
        />
      )}

      {loadingProfessionalRestrictions && (
        <S.Loading>
          <Spinner />
        </S.Loading>
      )}

      {!loadingProfessionalRestrictions && hasProfessionalRestrictions && (
        <NonRecurrentReservationBox
          patientUsername={patient.username}
          restrictions={restrictions} 
          handleNonRecurrentReservationDate={handleNonRecurrentReservationDate}
        />
      )}

      {showSubmitButton && (
        <S.ButtonWrapper>
          <Typography
            variant='content3'
            center
          >
            *Brasil (UTC−3) - Horário de Brasília Padrão 00h00 a 23h59
          </Typography>
          <Button
            variant='contained'
            uppercase={false}
            disabled={disableCreateReservationButton}
            onClick={handleCreateReservation}
          >
            Concluir
          </Button>
        </S.ButtonWrapper>
      )}

    </S.CreateReservationContainer>
  )
}